import { Component, OnInit } from '@angular/core';
import { AngularFirestore } from '@angular/fire/compat/firestore';
import { FormArray, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule } from '@angular/forms';
import { SafePipe } from '../../pipes/safe.pipe';
import { AuthService } from '../../services/auth.service';
import Swal from 'sweetalert2';

interface Tutorial {
  id: string;
  title: string;
  description: string;
}

interface TutorialChange {
  uid: string;
  timestamp: Date;
  action: 'created' | 'updated' | 'deleted';
  description: string;
}

interface TutorialsDocument {
  tutorials: Tutorial[];
  lastModified: Date;
  lastModifiedBy: string;
  changeHistory: TutorialChange[];
}

@Component({
  selector: 'app-edit-referral-tutorials',
  templateUrl: './edit-referral-tutorials.component.html',
  styleUrls: ['./edit-referral-tutorials.component.css'],
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, SafePipe]
})
export class EditReferralTutorialsComponent implements OnInit {
  tutorialsForm: FormGroup;
  isLoading: boolean = false;

  constructor(
    private afs: AngularFirestore,
    private fb: FormBuilder,
    private authService: AuthService
  ) {
    this.tutorialsForm = this.fb.group({
      tutorials: this.fb.array([])
    });
  }

  get tutorialsArray() {
    return this.tutorialsForm.get('tutorials') as FormArray;
  }

  ngOnInit(): void {
    this.loadTutorials();
  }

  loadTutorials() {
    this.isLoading = true;
    this.afs.collection('tutorials').doc('referral-tutorials').get().subscribe({
      next: (doc) => {
        if (doc.exists) {
          const data = doc.data() as TutorialsDocument;
          const tutorials = data?.tutorials || this.getDefaultTutorials();
          this.populateForm(tutorials);
        } else {
          this.populateForm(this.getDefaultTutorials());
        }
        this.isLoading = false;
      },
      error: (error) => {
        console.error('Error loading tutorials:', error);
        this.populateForm(this.getDefaultTutorials());
        this.isLoading = false;
      }
    });
  }

  getDefaultTutorials(): Tutorial[] {
    return [
      {
        id: '7ldfMfR5WAo',
        title: 'Tutorial de Referidos - Introducción',
        description: 'Aprende los fundamentos del programa de referidos de Domoblock'
      },
      {
        id: 'PJE6gP-zL_A',
        title: 'Cómo Referir a un Amigo',
        description: 'Guía paso a paso para referir nuevos usuarios a la plataforma'
      },
      {
        id: 'wpjd-xBDuMY',
        title: 'Ganancias del Programa de Referidos',
        description: 'Descubre cómo generar ingresos con el programa de referidos'
      }
    ];
  }

  populateForm(tutorials: Tutorial[]) {
    // Limpiar el FormArray
    while (this.tutorialsArray.length !== 0) {
      this.tutorialsArray.removeAt(0);
    }

    // Agregar cada tutorial al FormArray
    tutorials.forEach(tutorial => {
      this.tutorialsArray.push(this.fb.group({
        id: [tutorial.id, [Validators.required]],
        title: [tutorial.title, [Validators.required]],
        description: [tutorial.description, [Validators.required]]
      }));
    });
  }

  addTutorial() {
    this.tutorialsArray.push(this.fb.group({
      id: ['', [Validators.required]],
      title: ['', [Validators.required]],
      description: ['', [Validators.required]]
    }));
  }

  removeTutorial(index: number) {
    if (this.tutorialsArray.length > 1) {
      this.tutorialsArray.removeAt(index);
    } else {
      Swal.fire({
        icon: 'warning',
        title: 'No se puede eliminar',
        text: 'Debe haber al menos un tutorial'
      });
    }
  }

  async saveTutorials() {
    if (this.tutorialsForm.invalid) {
      this.tutorialsForm.markAllAsTouched();
      Swal.fire({
        icon: 'error',
        title: 'Formulario inválido',
        text: 'Por favor, completa todos los campos requeridos'
      });
      return;
    }

    try {
      const currentUser = await this.authService.userAuth.getValue();
      if (!currentUser) {
        Swal.fire({
          icon: 'error',
          title: 'Error de autenticación',
          text: 'No se pudo identificar al usuario'
        });
        return;
      }

      const tutorials = this.tutorialsForm.value.tutorials;
      const now = new Date();
      
      // Crear el cambio para el historial
      const change: TutorialChange = {
        uid: currentUser.uid,
        timestamp: now,
        action: 'updated',
        description: `Actualización de ${tutorials.length} tutoriales de referidos`
      };

      // Obtener el documento actual para preservar el historial existente
      const docRef = this.afs.collection('tutorials').doc('referral-tutorials');
      const doc = await docRef.get().toPromise();
      
      let existingHistory: TutorialChange[] = [];
      if (doc?.exists) {
        const data = doc.data() as TutorialsDocument;
        existingHistory = data.changeHistory || [];
      }

      // Agregar el nuevo cambio al historial
      const updatedHistory = [...existingHistory, change];

      // Guardar el documento completo
      await docRef.set({
        tutorials: tutorials,
        lastModified: now,
        lastModifiedBy: currentUser.uid,
        changeHistory: updatedHistory
      }, { merge: true });

      Swal.fire({
        icon: 'success',
        title: '¡Guardado exitosamente!',
        text: 'Los tutoriales de referidos han sido actualizados correctamente'
      });
    } catch (error) {
      console.error('Error saving tutorials:', error);
      Swal.fire({
        icon: 'error',
        title: 'Error al guardar',
        text: 'Ha ocurrido un error al guardar los tutoriales de referidos'
      });
    }
  }

  extractVideoId(url: string): string {
    if (!url) return '';
    
    // Patrones para diferentes formatos de URL de YouTube
    const patterns = [
      /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([^&\n?#]+)/,
      /youtube\.com\/watch\?.*v=([^&\n?#]+)/,
      /youtu\.be\/([^&\n?#]+)/
    ];

    for (const pattern of patterns) {
      const match = url.match(pattern);
      if (match) {
        return match[1];
      }
    }

    // Si no coincide con ningún patrón, asumir que es un ID directo
    return url;
  }

  onVideoIdChange(index: number) {
    const tutorialGroup = this.tutorialsArray.at(index);
    const videoIdControl = tutorialGroup.get('id');
    const videoId = videoIdControl?.value;
    
    if (videoId) {
      const extractedId = this.extractVideoId(videoId);
      if (extractedId !== videoId) {
        videoIdControl?.setValue(extractedId);
      }
    }
  }
}
